import { linSpace, percentile } from '../utils/math';
import { sampleBinomial, samplePoisson } from '../utils/random';
import {
  buildCumulativePointsFromCounts,
  buildDiscoveryCountsFromTimes,
  filterDiscoveryCounts,
} from '../series/discoverySeries';
import { computeChiSquareStatistic } from './diagnostics';
import { computeExpectedDiscoveryCounts, fitSolowSmithCore, solowSmithP, sampleSolowSmithTimes } from './solowSmith';

export const bootstrapCurveCIFromCounts = async (
  discoveryCounts,
  modelStartYear,
  fitFn,
  iterations,
  pickValue,
  options = {}
) => {
  if (!discoveryCounts || iterations <= 0 || !Number.isFinite(modelStartYear)) return null;
  const filtered = filterDiscoveryCounts(discoveryCounts, modelStartYear);
  if (filtered.length < 3) return null;
  const {
    randomFn = Math.random,
    curveFn = null,
    includeCurve = false,
    yieldEvery = 0,
    yieldFn = null,
    fitMethod = 'cumulative',
  } = options;
  const values = [];
  const years = includeCurve && curveFn ? filtered.map(item => item.year) : null;
  const curveSamples = includeCurve && curveFn ? years.map(() => []) : null;
  const minSamples = Math.max(10, Math.floor(iterations * 0.2));

  for (let i = 0; i < iterations; i += 1) {
    const simulated = filtered.map(item => ({
      year: item.year,
      count: samplePoisson(item.count, randomFn),
    }));
    const points = buildCumulativePointsFromCounts(simulated, modelStartYear);
    if (points.length < 3) {
      if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
        // eslint-disable-next-line no-await-in-loop
        await yieldFn();
      }
      continue;
    }
    const fit = fitFn(points, { discoveryCounts: simulated, modelStartYear, fitMethod });
    if (!fit || !fit.params) {
      if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
        // eslint-disable-next-line no-await-in-loop
        await yieldFn();
      }
      continue;
    }
    const value = pickValue(fit.params);
    if (!Number.isFinite(value)) {
      if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
        // eslint-disable-next-line no-await-in-loop
        await yieldFn();
      }
      continue;
    }

    if (curveSamples) {
      const predictions = [];
      let valid = true;
      for (let j = 0; j < years.length; j += 1) {
        const t = years[j] - modelStartYear;
        const predicted = curveFn(fit.params, t);
        if (!Number.isFinite(predicted)) {
          valid = false;
          break;
        }
        predictions.push(predicted);
      }
      if (!valid) {
        if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
          // eslint-disable-next-line no-await-in-loop
          await yieldFn();
        }
        continue;
      }
      predictions.forEach((predicted, index) => {
        curveSamples[index].push(predicted);
      });
    }

    values.push(value);

    if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
      // eslint-disable-next-line no-await-in-loop
      await yieldFn();
    }
  }

  if (values.length < minSamples) return null;
  const sortedValues = [...values].sort((a, b) => a - b);
  const result = {
    lower: percentile(sortedValues, 0.025),
    upper: percentile(sortedValues, 0.975),
    samples: values.length,
    values,
  };

  if (curveSamples) {
    const bands = [];
    for (let i = 0; i < curveSamples.length; i += 1) {
      if (curveSamples[i].length < minSamples) {
        return result;
      }
      const sorted = curveSamples[i].slice().sort((a, b) => a - b);
      bands.push([percentile(sorted, 0.025), percentile(sorted, 0.975)]);
    }
    result.curveBand = bands;
    result.curveYears = years;
  }

  return result;
};

export const bootstrapSolowSmithCI = async (
  baseFit,
  startYear,
  endYear,
  iterations,
  options = {}
) => {
  if (!baseFit || !baseFit.params || iterations <= 0) return null;
  const t0 = endYear - startYear;
  if (!(t0 > 0)) return null;
  const {
    varyN = false,
    randomFn = Math.random,
    includeCurve = false,
    yieldEvery = 0,
    yieldFn = null,
  } = options;
  const values = [];
  const years = includeCurve ? linSpace(startYear, endYear, endYear - startYear + 1) : null;
  const curveSamples = includeCurve ? years.map(() => []) : null;
  const minSamples = Math.max(10, Math.floor(iterations * 0.2));
  const total = Math.max(baseFit.n, Math.round(baseFit.total));

  for (let i = 0; i < iterations; i += 1) {
    const target = varyN ? sampleBinomial(total, baseFit.P0, randomFn) : baseFit.n;
    if (target < 3) continue;
    const times = sampleSolowSmithTimes(target, baseFit.params, t0, randomFn);
    if (!times) continue;
    const discovery = buildDiscoveryCountsFromTimes(times, startYear, endYear);
    const fit = fitSolowSmithCore(discovery, startYear, endYear, null);
    if (!fit || !Number.isFinite(fit.total)) {
      if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
        // eslint-disable-next-line no-await-in-loop
        await yieldFn();
      }
      continue;
    }
    if (curveSamples) {
      const predictions = [];
      let valid = true;
      for (let j = 0; j < years.length; j += 1) {
        const t = years[j] - startYear;
        const P = solowSmithP(t, fit.params.h, fit.params.b);
        const predicted = Number.isFinite(P) ? fit.total * P : null;
        if (!Number.isFinite(predicted)) {
          valid = false;
          break;
        }
        predictions.push(predicted);
      }
      if (!valid) {
        if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
          // eslint-disable-next-line no-await-in-loop
          await yieldFn();
        }
        continue;
      }
      predictions.forEach((predicted, index) => {
        curveSamples[index].push(predicted);
      });
    }
    values.push(fit.total);
    if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
      // eslint-disable-next-line no-await-in-loop
      await yieldFn();
    }
  }

  if (values.length < minSamples) return null;
  const sortedValues = [...values].sort((a, b) => a - b);
  const result = {
    lower: percentile(sortedValues, 0.025),
    upper: percentile(sortedValues, 0.975),
    samples: values.length,
    values,
  };

  if (curveSamples) {
    const bands = [];
    for (let i = 0; i < curveSamples.length; i += 1) {
      if (curveSamples[i].length < minSamples) {
        return result;
      }
      const sorted = curveSamples[i].slice().sort((a, b) => a - b);
      bands.push([percentile(sorted, 0.025), percentile(sorted, 0.975)]);
    }
    result.curveBand = bands;
    result.curveYears = years;
  }

  return result;
};

export const bootstrapSolowSmithGof = async (
  fit,
  discoveryCounts,
  startYear,
  endYear,
  iterations,
  options = {}
) => {
  if (!fit || !fit.params || iterations <= 0) return null;
  const t0 = endYear - startYear;
  if (!(t0 > 0)) return null;
  const expected = computeExpectedDiscoveryCounts(fit, startYear, endYear);
  if (!expected) return null;
  const observedStat = computeChiSquareStatistic(discoveryCounts, expected);
  if (!Number.isFinite(observedStat)) return null;
  const { randomFn = Math.random, yieldEvery = 0, yieldFn = null } = options;

  const total = Math.max(1, Math.round(fit.total));
  let extremeCount = 0;
  let samples = 0;
  for (let i = 0; i < iterations; i += 1) {
    const target = sampleBinomial(total, fit.P0, randomFn);
    if (target < 3) continue;
    const times = sampleSolowSmithTimes(target, fit.params, t0, randomFn);
    if (!times) continue;
    const simDiscovery = buildDiscoveryCountsFromTimes(times, startYear, endYear);
    const stat = computeChiSquareStatistic(simDiscovery, expected);
    if (!Number.isFinite(stat)) continue;
    if (stat >= observedStat) extremeCount += 1;
    samples += 1;
    if (yieldFn && yieldEvery > 0 && (i + 1) % yieldEvery === 0) {
      // eslint-disable-next-line no-await-in-loop
      await yieldFn();
    }
  }

  if (samples < Math.max(10, Math.floor(iterations * 0.2))) return null;
  return {
    pValue: (extremeCount + 1) / (samples + 1),
    samples,
  };
};

export const combineBootstrapSamples = (sampleSets, iterations, randomFn = Math.random, fixedTotal = 0) => {
  if (!sampleSets || iterations <= 0) return null;
  const validSets = sampleSets.filter(set => Array.isArray(set) && set.length > 0);
  if (!validSets.length) return null;
  const minSamples = Math.max(10, Math.floor(iterations * 0.2));

  const totals = [];
  for (let i = 0; i < iterations; i += 1) {
    let sum = fixedTotal;
    for (let j = 0; j < validSets.length; j += 1) {
      const set = validSets[j];
      const pick = set[Math.floor(randomFn() * set.length)];
      if (!Number.isFinite(pick)) continue;
      sum += pick;
    }
    if (Number.isFinite(sum)) totals.push(sum);
  }

  if (totals.length < minSamples) return null;
  totals.sort((a, b) => a - b);
  return {
    lower: percentile(totals, 0.025),
    upper: percentile(totals, 0.975),
    samples: totals.length,
  };
};
